import { Controller } from "@hotwired/stimulus";

// Drives the admin "Grant Complimentary Access" 4-step modal
// (Recipients → Products → Notify → Review).
//
// Both pickers accumulate into a list of removable chips. Recipients come from the shared
// single-select async typeahead: each pick becomes a chip and the field clears itself for
// the next one. Products come from a multi-select app-select, whose selected options are
// the source of truth — its chips are re-rendered from them. All state lives on the client
// until Confirm posts one JSON payload; the modal fully resets on close.
export default class extends Controller {
  static targets = [
    "stepper", "stepPill", "step",
    "error", "errorMessage",
    "recipientChips", "productSelect", "productChips",
    "notifyDetail", "channelInApp", "channelEmail", "reason",
    "previewRecipients", "previewProducts", "previewNotify", "previewGrants",
    "backBtn", "nextBtn", "confirmBtn",
    "chipTemplate"
  ];

  static values = { url: String };

  connect() {
    this.currentStep = 1;

    this._boundReset = this._reset.bind(this);
    this._boundSelect = this._onSelectChange.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);
    this.element.addEventListener("app-select:change", this._boundSelect);

    this._showStep();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
    this.element.removeEventListener("app-select:change", this._boundSelect);
  }

  // ── Delegated select handling ────────────────────────────────────────

  _onSelectChange(event) {
    if (event.target?.dataset?.role === "recipient") {
      this._onRecipientPicked(event);
      return;
    }

    if (this.hasProductSelectTarget && this.productSelectTarget.contains(event.target)) {
      this._renderProductChips();
    }
  }

  // ── Step navigation ──────────────────────────────────────────────────

  nextStep() {
    const error = this._validateStep(this.currentStep);
    if (error) {
      this._showError(error);
      return;
    }
    this._hideError();
    if (this.currentStep < 4) {
      this.currentStep += 1;
      this._showStep();
    }
  }

  prevStep() {
    this._hideError();
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      this._showStep();
    }
  }

  _showStep() {
    this.stepTargets.forEach((el) => {
      el.classList.toggle("d-none", Number(el.dataset.step) !== this.currentStep);
    });
    this.stepPillTargets.forEach((pill) => {
      const step = Number(pill.dataset.step);
      pill.classList.toggle("is-active", step === this.currentStep);
      pill.classList.toggle("is-complete", step < this.currentStep);
      if (step === this.currentStep) {
        pill.setAttribute("aria-current", "step");
      } else {
        pill.removeAttribute("aria-current");
      }
    });

    this.backBtnTarget.classList.toggle("d-none", this.currentStep === 1);
    this.nextBtnTarget.classList.toggle("d-none", this.currentStep === 4);
    this.confirmBtnTarget.classList.toggle("d-none", this.currentStep !== 4);

    if (this.currentStep === 4) this._renderPreview();

    this._focusFirstField();
  }

  _focusFirstField() {
    const step = this.stepTargets.find((el) => Number(el.dataset.step) === this.currentStep);
    const field = step?.querySelector("input:not([type=hidden]), button.app-select-button");
    if (field) requestAnimationFrame(() => field.focus());
  }

  _validateStep(step) {
    if (step === 1 && this._recipients().length === 0) {
      return "Add at least one recipient.";
    }
    if (step === 2 && this._products().length === 0) {
      return "Select at least one product.";
    }
    if (step === 3 && this._notifyEnabled() && this._notifyChannels().length === 0) {
      return "Choose at least one way to notify the recipients.";
    }
    return null;
  }

  // ── Step 1: recipients ───────────────────────────────────────────────

  // The typeahead holds one value at a time, so each pick is moved into a chip and the
  // field is emptied — otherwise the next search would replace the previous person.
  _onRecipientPicked(event) {
    const id = event.target.value;
    const label = event.detail?.label || "";
    this._clearAsyncTypeahead(event.target);
    if (!id) return;

    this._hideError();
    if (this._recipients().some((recipient) => recipient.id === id)) return;

    this.recipientChipsTarget.appendChild(this._buildChip(id, label));
  }

  // ── Step 2: products ─────────────────────────────────────────────────

  _renderProductChips() {
    this._hideError();
    this.productChipsTarget.innerHTML = "";
    this._selectedProductOptions().forEach((option) => {
      this.productChipsTarget.appendChild(this._buildChip(option.dataset.value, option.dataset.label));
    });
  }

  // ── Chips ────────────────────────────────────────────────────────────

  _buildChip(id, label) {
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    chip.dataset.id = id;
    chip.querySelector("[data-chip-label]").textContent = label;
    chip.querySelector(".complimentary-access-chip-remove").setAttribute("aria-label", `Remove ${label}`);
    return chip;
  }

  // A product chip mirrors a selected option, so removing it means deselecting that
  // option and letting the change event re-render the list.
  removeChip(event) {
    const chip = event.currentTarget.closest("[data-chip]");
    if (!chip) return;

    this._hideError();

    if (this.hasProductChipsTarget && this.productChipsTarget.contains(chip)) {
      this._productOption(chip.dataset.id)?.click();
      return;
    }

    chip.remove();
  }

  // ── Step 3: notify ───────────────────────────────────────────────────

  onNotifyEnabledChange() {
    const enabled = this._notifyEnabled();
    this._toggle(this.notifyDetailTarget, enabled);
    if (enabled) {
      this.channelInAppTarget.checked = true;
      this.channelEmailTarget.checked = true;
    }
    this._hideError();
  }

  onNotifyChannelChange() {
    this._hideError();
  }

  // ── Step 4: preview ──────────────────────────────────────────────────

  _renderPreview() {
    const recipients = this._recipients();
    const products = this._products();

    this.previewRecipientsTarget.textContent = recipients.map((recipient) => recipient.label).join(", ");
    this.previewProductsTarget.textContent = products.map((product) => product.label).join(", ");
    this.previewNotifyTarget.textContent = this._notifySummary();
    this.previewGrantsTarget.textContent =
      `${this._pluralize(recipients.length, "recipient")} × ${this._pluralize(products.length, "product")}`;
  }

  _notifySummary() {
    if (!this._notifyEnabled()) return "No notification";
    const channels = this._notifyChannels();
    const parts = [];
    if (channels.includes("in_app")) parts.push("In-app");
    if (channels.includes("email")) parts.push("Email");
    return parts.length ? parts.join(" · ") : "No notification";
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async submit() {
    const payload = {
      complimentary_access_grant: {
        recipient_ids: this._recipients().map((recipient) => recipient.id),
        payment_product_ids: this._products().map((product) => product.id),
        reason: this._reasonValue() || null,
        notify: {
          enabled: this._notifyEnabled() ? "1" : "0",
          channels: this._notifyChannels()
        }
      }
    };

    this.confirmBtnTarget.disabled = true;
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        window.location.assign(data.redirect_url);
      } else {
        this._showError((data.errors || ["Could not grant complimentary access."]).join(", "));
        this.confirmBtnTarget.disabled = false;
      }
    } catch (e) {
      this._showError("Something went wrong. Please try again.");
      this.confirmBtnTarget.disabled = false;
    }
  }

  // ── Reset ────────────────────────────────────────────────────────────

  _reset() {
    this.currentStep = 1;

    this.recipientChipsTarget.innerHTML = "";
    this._clearAsyncTypeahead(this._recipientInput);
    this._selectedProductOptions().forEach((option) => option.click());

    this._setRadio("complimentary_access[notify_enabled]", "no");
    this.channelInAppTarget.checked = false;
    this.channelEmailTarget.checked = false;
    this._toggle(this.notifyDetailTarget, false);
    if (this.hasReasonTarget) this.reasonTarget.value = "";

    this.confirmBtnTarget.disabled = false;
    this._hideError();
    this._showStep();
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  get _recipientInput() {
    return this.element.querySelector('[data-async-typeahead-target="hidden"][data-role="recipient"]');
  }

  _recipients() {
    return this._chipsIn(this.recipientChipsTarget);
  }

  _products() {
    return this.hasProductChipsTarget ? this._chipsIn(this.productChipsTarget) : [];
  }

  _chipsIn(container) {
    return Array.from(container.querySelectorAll("[data-chip]")).map((chip) => ({
      id: chip.dataset.id,
      label: chip.querySelector("[data-chip-label]").textContent
    }));
  }

  _selectedProductOptions() {
    if (!this.hasProductSelectTarget) return [];
    return Array.from(this.productSelectTarget.querySelectorAll(".app-select-option.app-select-option-selected"));
  }

  _productOption(id) {
    if (!this.hasProductSelectTarget) return null;
    return this.productSelectTarget.querySelector(`.app-select-option[data-value="${id}"]`);
  }

  _reasonValue() {
    return this.hasReasonTarget ? this.reasonTarget.value.trim() : "";
  }

  _notifyEnabled() {
    return this.element.querySelector('input[name="complimentary_access[notify_enabled]"]:checked')?.value === "yes";
  }

  _notifyChannels() {
    const channels = [];
    if (this.channelInAppTarget.checked) channels.push("in_app");
    if (this.channelEmailTarget.checked) channels.push("email");
    return channels;
  }

  _setRadio(name, value) {
    const input = this.element.querySelector(`input[name="${name}"][value="${value}"]`);
    if (input) input.checked = true;
  }

  _pluralize(count, noun) {
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  }

  _toggle(el, show) {
    if (el) el.classList.toggle("d-none", !show);
  }

  _showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }

  _hideError() {
    this.errorTarget.classList.add("d-none");
  }

  _clearAsyncTypeahead(hiddenInput) {
    if (!hiddenInput) return;
    const root = hiddenInput.closest("[data-controller~='async-typeahead']");
    const controller =
      root && this.application.getControllerForElementAndIdentifier(root, "async-typeahead");
    if (controller) {
      controller.clear({ dispatch: false });
      return;
    }
    hiddenInput.value = "";
  }
}
